import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Divider, Paper, useMediaQuery, useTheme } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useAppSelector } from '@store/hooks';
import { selectSidebarOpen } from '@store/selectors/uiSelectors';

import { useToolbar } from '../store/hooks';
import { type ActionConfig } from '../types';
import { isToolbarRoute } from '../utils/routeEligibility';

interface ToolbarProps {
  actions: ActionConfig[];
}

export const Toolbar: React.FC<ToolbarProps> = ({ actions }) => {
  const theme = useTheme();
  const location = useLocation();
  const sidebarOpen = useAppSelector(selectSidebarOpen);
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const isExtraLargeScreen = useMediaQuery(theme.breakpoints.up('xl'));
  const { isVisible, scrollControlEnabled, actionContext, preferences, scrollContainerSelector } =
    useToolbar();
  const { toolbar } = useEventTracking();
  const [scrollVisible, setScrollVisible] = useState(true);
  const [delayedShow, setDelayedShow] = useState(false);
  const lastScrollYRef = useRef(0);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const showDelayTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const rafIdRef = useRef<number | undefined>(undefined);
  const hasTrackedShow = useRef(false);

  // Check if we're on a note view
  const isNoteView = Boolean(location.pathname.match(/^\/note\/[^/]+$/));
  const isEligibleRoute = isToolbarRoute(location.pathname);

  // Only shift position on note views when sidebar is open and viewport is wider than lg breakpoint
  const shouldShift = isNoteView && sidebarOpen && isLargeScreen;
  const leftPosition = shouldShift ? '37.5%' : '50%';

  // Force compact mode on note views when sidebar is open, unless on extra large screens
  const effectiveCompactMode = preferences.compactMode || (shouldShift && !isExtraLargeScreen);

  // Scroll behavior
  useEffect(() => {
    if (!scrollControlEnabled) {
      setScrollVisible(true);
      return;
    }

    // Fallback chain for scroll container
    const getScrollContainer = () => {
      // Try custom selector first
      if (scrollContainerSelector) {
        const element = document.querySelector(scrollContainerSelector);
        if (element) return element;
      }

      // Default fallbacks
      const rootMain = document.getElementById('root-main');
      if (rootMain) return rootMain;

      // Ultimate fallback to document.body
      return document.body;
    };

    const scrollContainer = getScrollContainer();

    // Reset scroll position when container changes
    lastScrollYRef.current = 0;

    const handleScroll = () => {
      const currentScrollY =
        scrollContainer === document.body ? window.scrollY : scrollContainer.scrollTop;

      // Clear existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Hide when scrolling down, show when scrolling up
      if (currentScrollY > lastScrollYRef.current && currentScrollY > 100) {
        setScrollVisible(false);
      } else if (currentScrollY < lastScrollYRef.current) {
        setScrollVisible(true);
      }

      // Show after stopping scroll
      scrollTimeoutRef.current = setTimeout(() => {
        setScrollVisible(true);
      }, 1000);

      lastScrollYRef.current = currentScrollY;
    };

    // Use requestAnimationFrame for better performance
    const throttledHandleScroll = () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
      rafIdRef.current = requestAnimationFrame(handleScroll);
    };

    scrollContainer.addEventListener('scroll', throttledHandleScroll, { passive: true });

    return () => {
      scrollContainer.removeEventListener('scroll', throttledHandleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, [scrollControlEnabled, scrollContainerSelector]);

  // Combine programmatic visibility with scroll visibility
  const shouldShow = isVisible && scrollVisible && isEligibleRoute;

  // Handle delayed show with immediate hide
  useEffect(() => {
    if (shouldShow) {
      // Delay showing by 500ms
      showDelayTimeoutRef.current = setTimeout(() => {
        setDelayedShow(true);

        // Track toolbar shown event
        if (!hasTrackedShow.current) {
          const viewType = isNoteView
            ? 'note'
            : location.pathname.startsWith('/encounters/')
              ? 'encounter'
              : 'other';
          toolbar.shown({
            view_type: viewType,
            has_patient: !!actionContext?.patientId,
          });
          hasTrackedShow.current = true;
        }
      }, 500);
    } else {
      // Hide immediately
      setDelayedShow(false);
      hasTrackedShow.current = false; // Reset tracking when hidden
      // Clear any pending show timeout
      if (showDelayTimeoutRef.current) {
        clearTimeout(showDelayTimeoutRef.current);
      }
    }

    return () => {
      if (showDelayTimeoutRef.current) {
        clearTimeout(showDelayTimeoutRef.current);
      }
    };
  }, [shouldShow, isNoteView, location.pathname, actionContext?.patientId, toolbar]);

  const renderActions = () => {
    return actions.map((action, index) => {
      const Component = action.component;

      return (
        <React.Fragment key={action.id}>
          <Component context={actionContext} compact={effectiveCompactMode} />
          {action.dividerAfter && index < actions.length - 1 && (
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1 }} />
          )}
        </React.Fragment>
      );
    });
  };

  const positionStyles =
    preferences.position === 'top' ? { top: 24 } : { bottom: { xs: 74, md: 48 } };
  const isTop = preferences.position === 'top';

  // Visibility is global state and can remain true across a route transition.
  // Route eligibility is the hard boundary that prevents cross-page leakage.
  if (!isEligibleRoute) return null;

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        ...positionStyles,
        left: leftPosition,
        transform: `translateX(-50%) translateY(${delayedShow ? '0' : isTop ? '-150%' : '150%'})`,
        opacity: delayedShow ? 1 : 0,
        zIndex: theme.zIndex.speedDial,
        backgroundColor: 'background.paper',
        borderRadius: 2,
        overflow: 'hidden',
        transition:
          'left 0.3s ease-in-out, transform 0.3s cubic-bezier(0.4, 0.0, 0.2, 1), opacity 0.3s ease-in-out',
        maxWidth: '90vw',
        pointerEvents: delayedShow ? 'auto' : 'none',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1.5,
          py: 0.5,
        }}
      >
        {renderActions()}
      </Box>
    </Paper>
  );
};
